import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
} from '@web/shared/ui';
import { Nillable, valueOrDefault } from '@web/utils';
import {
  AdminShippingSettingsFormData,
  AdminShippingSettingsFormGroup,
} from '../../models/admin-shipping-settings-form-data';

@Component({
  selector: 'app-admin-shipping-settings-form',
  imports: [
    Button,
    CommonModule,
    FormControlComponent,
    InputNumber,
    InputText,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './admin-shipping-settings-form.component.html',
  styleUrl: './admin-shipping-settings-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminShippingSettingsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);

  public readonly data = input.required<AdminShippingSettingsFormData>();
  public readonly submitClickEvent = output<AdminShippingSettingsFormData>();

  protected readonly form = this.buildForm();

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private toData(): AdminShippingSettingsFormData {
    const data = this.form.getRawValue();

    return new AdminShippingSettingsFormData({
      labelFetchRetries: valueOrDefault(data.labelFetchRetries, 1),
      labelsInFile: valueOrDefault(data.labelsInFile, 1),
      maxPackagesInNationalShipment: valueOrDefault(data.maxPackagesInNationalShipment, 1),
      maxPackagesInInternationalShipment: valueOrDefault(data.maxPackagesInInternationalShipment, 1),
      senderFirstName: valueOrDefault(data.senderFirstName, ''),
      senderLastName: valueOrDefault(data.senderLastName, ''),
      senderEmail: valueOrDefault(data.senderEmail, ''),
      senderPhonePrefix: valueOrDefault(data.senderPhonePrefix, ''),
      senderPhoneNumber: valueOrDefault(data.senderPhoneNumber, ''),
      senderCompany: data.senderCompany,
      warehouseCountryCode: valueOrDefault(data.warehouseCountryCode, ''),
      warehouseCity: valueOrDefault(data.warehouseCity, ''),
      warehouseStreet: valueOrDefault(data.warehouseStreet, ''),
      warehousePostCode: valueOrDefault(data.warehousePostCode, ''),
      warehouseHouseNumber: valueOrDefault(data.warehouseHouseNumber, ''),
      warehouseFlatNumber: data.warehouseFlatNumber,
    });
  }

  private buildForm(): AdminShippingSettingsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      labelFetchRetries: this.builder.control<Nillable<number>>(null, [Validators.required, Validators.min(1)]),
      labelsInFile: this.builder.control<Nillable<number>>(null, [Validators.required, Validators.min(1)]),
      maxPackagesInNationalShipment: this.builder.control<Nillable<number>>(null, [Validators.required, Validators.min(1)]),
      maxPackagesInInternationalShipment: this.builder.control<Nillable<number>>(null, [Validators.required, Validators.min(1)]),
      senderFirstName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.SenderFirstNameMaxLength)]),
      senderLastName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.SenderLastNameMaxLength)]),
      senderEmail: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.email, Validators.maxLength(AdminShippingSettingsFormData.SenderEmailMaxLength)]),
      senderPhonePrefix: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.SenderPhonePrefixMaxLength)]),
      senderPhoneNumber: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.SenderPhoneNumberMaxLength)]),
      senderCompany: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminShippingSettingsFormData.SenderCompanyMaxLength)]),
      warehouseCountryCode: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.WarehouseCountryCodeMaxLength)]),
      warehouseCity: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.WarehouseCityMaxLength)]),
      warehouseStreet: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.WarehouseStreetMaxLength)]),
      warehousePostCode: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.WarehousePostCodeMaxLength)]),
      warehouseHouseNumber: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingSettingsFormData.WarehouseHouseNumberMaxLength)]),
      warehouseFlatNumber: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminShippingSettingsFormData.WarehouseFlatNumberMaxLength)]),
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue(
      {
        labelFetchRetries: data.labelFetchRetries,
        labelsInFile: data.labelsInFile,
        maxPackagesInNationalShipment: data.maxPackagesInNationalShipment,
        maxPackagesInInternationalShipment: data.maxPackagesInInternationalShipment,
        senderFirstName: data.senderFirstName,
        senderLastName: data.senderLastName,
        senderEmail: data.senderEmail,
        senderPhonePrefix: data.senderPhonePrefix,
        senderPhoneNumber: data.senderPhoneNumber,
        senderCompany: data.senderCompany,
        warehouseCountryCode: data.warehouseCountryCode,
        warehouseCity: data.warehouseCity,
        warehouseStreet: data.warehouseStreet,
        warehousePostCode: data.warehousePostCode,
        warehouseHouseNumber: data.warehouseHouseNumber,
        warehouseFlatNumber: data.warehouseFlatNumber,
      },
      { emitEvent: false },
    );
  }
}
